import { useContext, useMemo } from "react";
import { Switch, Route, Redirect } from "react-router-dom";

import AuthorizedRoute from "../../authorized-route";
import AccessoryRoute from "../../accessory-route";
import PermissionRoute from "../../permission-route";
// import OwnerRoute from "../../owned-route.js";

import UnauthorizedPage from "../unauthorized";

import HomePage from "./home-page";
import TrialExpiredPage from "../trial";
import Guests from "../guests";
import Contacts from "../contacts";
import Messages from "../messages";
import DestinationGuide from "../destination_guide";
import DestinationGuideDetail from "../destination_guide/destination_detail";
import PropertyGuide from "../property_guide";
import AddOnEditor from "../add_on/index.js";
import Scheduler from "../scheduler";
import Alerts from "../alerts";
import SavedResponses from "../saved_responses";
import MessageTemplates from "../message_templates";
import ExtendGuestStay from "../extend_guest_stay";
import Orders from "../orders";
import Payments from "../payments";
import WorkOrder from "../work_order";
import WorkOrderCalendar from "../work_order/work_order_calendar";
import WorkOrderUpdate from "../work_order/work_order_update";
import Tasks from "../operations/tasks";
import Surveys from "../surveys";
import Reports from "../reports";
import Notes from "../notes";
import Team from "../team";
import RolesPage from "../permissions/roles_page";
import RoleEditorPage from "../permissions/role_editor_page";
import { usePermissionsStore } from "../permissions/permissions_store";
import UserGroups from "../user_groups";
import Units from "../units";
import UnitDetail from "../units/unit_detail";
import Macros from "../macros";
import PropertyFieldValues from "../macros/property_field_values";
import ChatbotFaqs from "../chatbot_faqs";
import ChatbotUnansweredQuestions from "../chatbot_unanswered_questions";
import Settings from "../settings";
import Profile from "../profile";
import Billing from "../billing";
import NewSubscription from "../billing/new_subscription";
import GuestEmails from "../subscriptions";
import MessageComposer from "../common/AIMessageComposer/index.js";
import Onboarding from "../onboarding";
import GettingStarted from "../getting_started";
import WhatsNew from "../whats_new";
import FeatureDiscovery from "../features";
import OpsDashboard from "../operations";
import OpsChecklists from "../operations/checklists";
import OpsInspections from "../operations/inspections";
import OpsStorageLocations from "../operations/storage/locations";
import OpsStorageItems from "../operations/storage/items";
import OpsStorageStock from "../operations/storage/stock";
import OpsStorageMovements from "../operations/storage/movements";
import OpsInventoryHealth from "../operations/storage/health";
import OpsInventoryValueReport from "../operations/storage/reports/value";
import OpsInventoryLossReport from "../operations/storage/reports/loss";
import OpsLowStockReport from "../operations/storage/reports/low_stock";
import OpsStandardsStructure from "../operations/standards/standards_structure";
import OpsPropertySetup from "../operations/property_setup";
import Holidays from "../holidays";
import TodaysBookings from "../guests/todays_bookings.js";
import Zones from "../zones/index.js";
import WorkSchedules from "../team/work_schedules";
import AvailabilityCalendar from "../team/availability_calendar";
import CRM from "../crm";
import CRMOpenTasks from "../crm/open-tasks";
import CRMPipelines from "../crm/pipelines";
import CustomFields from "../settings/CustomFields";
import LockManagementPortal from "../lock_management_portal";
import { AppContexts } from "../../providers";
import GuideDesigner from "../guide_designer";
import FEATURES from "../../config/features";

const OPS_TASK_WORK_ORDER_CONFIG = {
  pageTitle: "Tasks",
  documentTitle: "Tasks - RueBaRue",
  itemLabel: "Task",
  itemLabelPlural: "Tasks",
  addTextLineFeature: "Tasks",
  emptyStateLabel: "tasks",
  detailBasePath: "/operations/tasks",
  showTaskType: true,
  showRecurrence: true,
  showPropertyManagerFilter: true,
  taskTypeLabel: "Type*",
  editModuleKey: "operations.work_orders",
};

const OperationsTaskWorkOrder = (props) => (
  <WorkOrder {...props} listConfig={OPS_TASK_WORK_ORDER_CONFIG} />
);

const RedirectToInventoryOverview = () => <Redirect to="/operations/storage" />;
const RedirectToWorkOrders = () => <Redirect to="/work-orders" />;
const RedirectToOperations = () => <Redirect to="/operations" />;

const AppLayout = (props) => {
  const { user: account } = useContext(AppContexts.AuthContext);
  const { canView, isOwner, permsLoaded } = usePermissionsStore();
  const hasFlag = (flagName) => {
    const accountFlags = account?.flags || [];
    return accountFlags.some((f) => f.name === flagName);
  };
  // Tasks is now generally available for every account. The legacy Work Order
  // routes/components are intentionally kept below (the /work-orders route just
  // redirects to /operations/tasks) so old links/bookmarks keep working and we
  // retain a fast rollback path: set this back to hasFlag("ops-beta").
  const hasOpsTasks = true;
  const hasOnlyMyTasksAccess = useMemo(() => {
    if (isOwner || !permsLoaded || !canView("operations.tasks")) {
      return false;
    }

    return [
      "operations.calendar",
      "operations.work_orders",
      "operations.inspections",
      "operations.inventory",
      "operations.housekeeping",
      "operations.checklists",
      "operations.standards",
      "operations.work_schedules",
    ].every((moduleKey) => !canView(moduleKey));
  }, [canView, isOwner, permsLoaded]);

  return (
    <Switch>
      {hasOpsTasks && (
        <Route
          exact
          path="/tasks/calendar"
          render={({ location }) => (
            <Redirect to={{ pathname: "/operations/calendar", search: location.search }} />
          )}
        />
      )}
      <PermissionRoute exact path="/tasks/:id([0-9]+)" module="operations.tasks" component={WorkOrderUpdate} />
      <PermissionRoute path="/tasks" module="operations.tasks" component={Tasks} />
      <PermissionRoute path="/work-orders" module="operations.work_orders" component={WorkOrder} />
      <AuthorizedRoute path="/tasks" component={Tasks} />
      {!account && (
        <Route
          exact
          path="/work-orders/:slug/:id"
          component={WorkOrderUpdate}
        />
      )}
      {!hasOpsTasks && <Route path="/work-orders" component={WorkOrder} />}
      {hasOpsTasks && (
        <Route
          path="/work-orders"
          render={({ location }) => (
            <Redirect
              to={{
                pathname: location.pathname.replace(/^\/work-orders/, "/operations/tasks"),
                search: location.search,
              }}
            />
          )}
        />
      )}
      <Route path="/expired" component={TrialExpiredPage} />

      <Route path="/unauthorized" component={UnauthorizedPage} />

      <AuthorizedRoute exact path="/" component={HomePage} />
      <PermissionRoute path="/guests"    module="messaging.guests"   component={Guests} />
      <PermissionRoute path="/contacts"  module="messaging.contacts" component={Contacts} />
      <PermissionRoute path="/messages"  module="messaging.messages" component={Messages} />
      <PermissionRoute path="/properties" module="guides.home" component={PropertyGuide} />
      <PermissionRoute path="/areas" module="guides.area" component={DestinationGuide} />
      <PermissionRoute
        path="/areas_detail/:destination_id"
        module="guides.area"
        component={DestinationGuideDetail}
      />
      <PermissionRoute
        module="guides.master"
        exact
        path="/master-home-guide"
        component={() => <Redirect to="/guide-designer?section=home" />}
      />
      <AccessoryRoute path="/extras" module="guides.extras" component={AddOnEditor} />
      <PermissionRoute path="/subscriptions" module="guides.master" component={GuestEmails} />
      <PermissionRoute path="/scheduler"          module="messaging.scheduler"   component={Scheduler} />
      <PermissionRoute path="/alerts"             module="messaging.alerts"      component={Alerts} />
      <PermissionRoute path="/saved-responses"    module="messaging.responses"   component={SavedResponses} />
      <PermissionRoute path="/message-templates"  module="messaging.templates"   component={MessageTemplates} />
      <PermissionRoute path="/extend-guest-stay"  module="messaging.extend_stay" component={ExtendGuestStay} />
      <AccessoryRoute path="/orders" module="guides.payments" component={Orders} />
      <AccessoryRoute path="/payments" module="guides.payments" component={Payments} />
      <PermissionRoute path="/surveys"  module="messaging.surveys"  component={Surveys} />
      <PermissionRoute path="/reports" module="reports" component={Reports} />
      <PermissionRoute path="/notes" module="crm.notes" component={Notes} />
      <AuthorizedRoute path="/profile"  component={Profile} />
      <AuthorizedRoute path="/new-subscription" component={NewSubscription} />
      <PermissionRoute path="/settings/chat" module="messaging.chatbot" component={Settings} />
      <PermissionRoute path="/settings/five-star" module="messaging.surveys" component={Settings} />
      <PermissionRoute path="/settings/smart-locks" module="admin.integrations" component={Settings} />
      <PermissionRoute path="/settings" module="admin.settings" component={Settings} />
      <PermissionRoute path="/team"  module="admin.users" component={Team} />
      <PermissionRoute exact path="/roles"          module="admin.roles" component={RolesPage} />
      <PermissionRoute exact path="/roles/:roleId"  module="admin.roles" component={RoleEditorPage} />
      <PermissionRoute path="/billing" module="admin.billing" component={Billing} />
      <PermissionRoute path="/units" module={["admin.settings", "operations.work_orders"]} component={Units} />
      <PermissionRoute path="/unit_detail/:rental_id" module={["admin.settings", "operations.work_orders"]} component={UnitDetail} />
      <PermissionRoute path="/macros" module="admin.settings" component={Macros} />
      {hasFlag("crm") && (
        <PermissionRoute path="/custom-fields" module="crm.custom_fields" component={CustomFields} />
      )}
      <PermissionRoute path="/property-field-values" module="admin.settings" component={PropertyFieldValues} />
      <PermissionRoute path="/chatbot-faqs" module="messaging.chatbot" component={ChatbotFaqs} />
      <PermissionRoute
        path="/unanswered-questions"
        module="messaging.chatbot"
        component={ChatbotUnansweredQuestions}
      />
      <PermissionRoute path="/ai-assistant" module="messaging.ai" component={MessageComposer} />
      <AuthorizedRoute path="/onboarding" component={Onboarding} />
      <AuthorizedRoute path="/getting-started" component={GettingStarted} />
      <AuthorizedRoute path="/whats-new" component={WhatsNew} />
      <AuthorizedRoute exact path="/features/:featureId?" component={FeatureDiscovery} />
      <PermissionRoute exact path="/operations" module="operations.calendar" component={OpsDashboard} />
      <PermissionRoute path="/operations/checklists" module="operations.checklists" component={OpsChecklists} />
      <PermissionRoute path="/operations/inspection-board" module="operations.inspections" component={OpsInspections} />
      <PermissionRoute exact path="/operations/storage" module="operations.inventory" component={OpsInventoryHealth} />
      <PermissionRoute exact path="/operations/storage/health" module="operations.inventory" component={RedirectToInventoryOverview} />
      <PermissionRoute path="/operations/storage/locations" module="operations.inventory" component={OpsStorageLocations} />
      <PermissionRoute path="/operations/storage/items" module="operations.inventory" component={OpsStorageItems} />
      <PermissionRoute path="/operations/storage/stock" module="operations.inventory" component={OpsStorageStock} />
      <PermissionRoute path="/operations/storage/movements" module="operations.inventory" component={OpsStorageMovements} />
      <PermissionRoute path="/operations/storage/reports/value" module="operations.inventory" component={OpsInventoryValueReport} />
      <PermissionRoute path="/operations/storage/reports/loss" module="operations.inventory" component={OpsInventoryLossReport} />
      <PermissionRoute path="/operations/storage/reports/low-stock" module="operations.inventory" component={OpsLowStockReport} />
      {hasOpsTasks && FEATURES.CLEANING_STANDARDS_ENABLED && (
        <PermissionRoute path="/operations/standards-structure" module="operations.standards" level="edit" component={OpsStandardsStructure} />
      )}
      {hasOpsTasks && FEATURES.PROPERTY_SETUP_ENABLED && (
        <PermissionRoute path="/operations/property-setup" module="operations.standards" level="edit" component={OpsPropertySetup} />
      )}
      {hasOpsTasks && (
        <PermissionRoute
          path="/operations/tasks"
          module="operations.tasks"
          component={hasOnlyMyTasksAccess ? Tasks : OperationsTaskWorkOrder}
        />
      )}
      {hasOpsTasks && (
        <PermissionRoute exact path="/operations/calendar" module="operations.calendar" component={WorkOrderCalendar} />
      )}
      {!hasOpsTasks && (
        <PermissionRoute path="/operations/tasks" module="operations.tasks" component={RedirectToWorkOrders} />
      )}
      {!hasOpsTasks && (
        <PermissionRoute exact path="/operations/calendar" module="operations.calendar" component={RedirectToWorkOrders} />
      )}
      {(!hasOpsTasks || !FEATURES.CLEANING_STANDARDS_ENABLED) && (
        <PermissionRoute path="/operations/standards-structure" module="operations.standards" level="edit" component={RedirectToOperations} />
      )}
      {(!hasOpsTasks || !FEATURES.PROPERTY_SETUP_ENABLED) && (
        <PermissionRoute path="/operations/property-setup" module="operations.standards" level="edit" component={RedirectToOperations} />
      )}
      <PermissionRoute path="/zones" module="admin.users" component={Zones} />
      <PermissionRoute path="/todays_bookings" module="operations.calendar" component={TodaysBookings} />
      <PermissionRoute path="/holidays" module="admin.settings" component={Holidays} />
      <PermissionRoute path="/work-schedules" module="operations.work_schedules" component={WorkSchedules} />
      <PermissionRoute path="/availability-calendar" module="operations.work_schedules" component={AvailabilityCalendar} />
      <PermissionRoute exact path="/crm/open-tasks" module="operations.tasks" component={CRMOpenTasks} />
      <PermissionRoute path="/crm/pipelines/:pipelineType?" module="crm.pipelines"  component={CRMPipelines} />
      <PermissionRoute path="/crm/:category/:id(\d+)/:subpage?"       module="crm.contacts"   component={CRM} />
      <PermissionRoute path="/crm/:category?"               module="crm.contacts"   component={CRM} />
      <PermissionRoute path="/smart-locks" module="admin.integrations" component={LockManagementPortal} />
      <PermissionRoute path="/guide-designer" module="guides.master" level="edit" component={GuideDesigner} />
    </Switch>
  );
};

export default AppLayout;
